/**
 * SubscriptionsDialog — shows the current user's bell-subscribed creators.
 * Opens as a bottom sheet (full-screen modal on mobile).
 * Each row has avatar, @username, a switch to unsubscribe, and is tappable to
 * navigate to that creator's profile.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, User, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Switch } from '@/components/ui/switch';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import {
  subscribeManySubscriptionsCreators,
  deleteSubscriptionsCreators,
  SubscriptionsCreatorsResponse,
} from '@/lib/collections/subscriptions';
import { subscribeUserAvatars, UserAvatarsResponse } from '@/lib/collections/userAvatars';
import { subscribeUserProfiles, UserProfilesResponse } from '@/lib/collections/userProfiles';

// ─── Single creator row ────────────────────────────────────────────────────────

interface CreatorRowProps {
  subscription: SubscriptionsCreatorsResponse;
  subscriberAddress: string;
  onRemoved: (creatorAddress: string) => void;
  onNavigate: (creatorAddress: string) => void;
}

const CreatorRow: React.FC<CreatorRowProps> = ({
  subscription,
  subscriberAddress,
  onRemoved,
  onNavigate,
}) => {
  const creatorAddress = subscription.creator;
  const [removing, setRemoving] = useState(false);

  const { data: avatarData } = useRealtimeData<UserAvatarsResponse | null>(
    subscribeUserAvatars,
    !!creatorAddress,
    creatorAddress,
  );

  const { data: profileData } = useRealtimeData<UserProfilesResponse | null>(
    subscribeUserProfiles,
    !!creatorAddress,
    creatorAddress,
  );

  const avatarUrl = avatarData?.avatarUrl ?? null;
  const username = profileData?.username ?? null;
  const shortAddr = `${creatorAddress.slice(0, 4)}...${creatorAddress.slice(-4)}`;
  const displayName = username ? `@${username}` : shortAddr;

  const handleToggle = async (checked: boolean) => {
    if (checked) return; // Already on — switching off deletes
    if (removing) return;
    setRemoving(true);
    const ok = await deleteSubscriptionsCreators(subscriberAddress, creatorAddress);
    if (ok) {
      onRemoved(creatorAddress);
    } else {
      setRemoving(false);
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: removing ? 0.4 : 1, x: 0 }}
      exit={{ opacity: 0, height: 0, marginBottom: 0, overflow: 'hidden' }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '11px 0',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        cursor: 'pointer',
      }}
    >
      {/* Avatar — tappable, navigates to profile */}
      <div
        onClick={() => onNavigate(creatorAddress)}
        style={{
          flexShrink: 0,
          width: 42,
          height: 42,
          borderRadius: '50%',
          background: 'rgba(167,139,250,0.12)',
          border: '1.5px solid rgba(167,139,250,0.3)',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={displayName}
            loading="lazy"
            decoding="async"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <User size={20} color="rgba(167,139,250,0.7)" strokeWidth={1.75} />
        )}
      </div>

      {/* Name — tappable, navigates to profile */}
      <div
        onClick={() => onNavigate(creatorAddress)}
        style={{ flex: 1, minWidth: 0 }}
      >
        <div style={{
          fontSize: 14,
          fontWeight: 700,
          color: '#f0f0f5',
          fontFamily: "'Syne', sans-serif",
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {displayName}
        </div>
        {username && (
          <div style={{
            fontSize: 11,
            color: 'rgba(240,240,245,0.4)',
            fontFamily: "'DM Mono', monospace",
            marginTop: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {shortAddr}
          </div>
        )}
      </div>

      {/* Subscribe switch — toggling off = delete */}
      <Switch
        checked={!removing}
        onCheckedChange={handleToggle}
        disabled={removing}
        style={{ flexShrink: 0 }}
      />
    </motion.div>
  );
};

// ─── Main dialog component ────────────────────────────────────────────────────

interface SubscriptionsDialogProps {
  open: boolean;
  onClose: () => void;
  userAddress: string;
}

export const SubscriptionsDialog: React.FC<SubscriptionsDialogProps> = ({
  open,
  onClose,
  userAddress,
}) => {
  const navigate = useNavigate();

  // Real-time list of all subscriptions for this user
  const { data: subscriptions } = useRealtimeData<SubscriptionsCreatorsResponse[]>(
    subscribeManySubscriptionsCreators,
    open && !!userAddress,
    userAddress,
  );

  // Track locally-removed creators for optimistic UI (row slides out immediately)
  const [removedCreators, setRemovedCreators] = useState<Set<string>>(new Set());

  const visibleSubscriptions = (subscriptions ?? []).filter(
    (s) => !removedCreators.has(s.creator),
  );

  const handleRemoved = (creatorAddress: string) => {
    setRemovedCreators((prev) => new Set([...prev, creatorAddress]));
  };

  const handleNavigate = (creatorAddress: string) => {
    onClose();
    navigate(`/user/${creatorAddress}`);
  };

  // Reset removed set when dialog opens so fresh data is shown
  React.useEffect(() => {
    if (open) setRemovedCreators(new Set());
  }, [open]);

  // Lock body scroll while dialog is open so the background doesn't move
  React.useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    const prevOverflow = document.body.style.overflow;
    const prevPosition = document.body.style.position;
    const prevTop = document.body.style.top;
    const prevWidth = document.body.style.width;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.position = prevPosition;
      document.body.style.top = prevTop;
      document.body.style.width = prevWidth;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key="subscriptions-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{
              position: 'fixed', inset: 0, zIndex: 4000,
              background: 'rgba(0,0,0,0.65)',
              backdropFilter: 'blur(6px)',
            }}
          />

          {/* Sheet from bottom */}
          <motion.div
            key="subscriptions-sheet"
            initial={{ y: '100%', opacity: 0.6 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', stiffness: 380, damping: 34 }}
            style={{
              position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 4001,
              background: '#141420',
              borderRadius: '20px 20px 0 0',
              border: '1px solid rgba(255,255,255,0.09)',
              borderBottom: 'none',
              padding: '0 0 calc(env(safe-area-inset-bottom) + 28px)',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Handle bar */}
            <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 0', flexShrink: 0 }}>
              <div style={{ width: 36, height: 4, borderRadius: 4, background: 'rgba(255,255,255,0.15)' }} />
            </div>

            {/* Header */}
            <div style={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 20px 7px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 10,
                  background: 'rgba(236,72,153,0.18)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Bell size={16} color="#ec4899" strokeWidth={1.8} />
                </div>
                <div>
                  <h3 style={{
                    margin: 0,
                    fontSize: 17, fontWeight: 800, color: '#f0f0f5',
                    fontFamily: "'Syne', sans-serif", letterSpacing: '-0.02em',
                  }}>
                    Subs
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: 11, color: 'rgba(240,240,245,0.4)',
                    fontFamily: "'DM Mono', monospace", marginTop: 1,
                  }}>
                    {visibleSubscriptions.length} creator{visibleSubscriptions.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>

              <button
                onClick={onClose}
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  width: 30, height: 30,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'rgba(240,240,245,0.6)',
                }}
              >
                <X size={15} />
              </button>
            </div>

            {/* Content — scrollable */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px' }}>
              {visibleSubscriptions.length === 0 ? (
                <div style={{
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  padding: '48px 24px', gap: 12,
                  textAlign: 'center',
                }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: '50%',
                    background: 'rgba(236,72,153,0.1)',
                    border: '1.5px solid rgba(236,72,153,0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Bell size={24} color="rgba(236,72,153,0.5)" strokeWidth={1.5} />
                  </div>
                  <p style={{
                    margin: 0,
                    fontSize: 15, fontWeight: 700, color: 'rgba(240,240,245,0.5)',
                    fontFamily: "'Syne', sans-serif",
                  }}>
                    No subscriptions yet
                  </p>
                  <p style={{
                    margin: 0,
                    fontSize: 12, color: 'rgba(240,240,245,0.3)',
                    fontFamily: "'Syne', sans-serif", lineHeight: 1.5,
                    maxWidth: 240,
                  }}>
                    Visit a creator's profile and tap the Bell button to get notified of new uploads.
                  </p>
                </div>
              ) : (
                <AnimatePresence initial={false}>
                  {visibleSubscriptions.map((sub) => (
                    <CreatorRow
                      key={sub.creator}
                      subscription={sub}
                      subscriberAddress={userAddress}
                      onRemoved={handleRemoved}
                      onNavigate={handleNavigate}
                    />
                  ))}
                </AnimatePresence>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SubscriptionsDialog;
